import { atom, selector } from 'recoil';
import { FLAG_VIEW_MODE, IAdminFeatureFlag, IFlagPreviewer } from '@types';

/* eslint-disable */
const keys = {
	FLAGS:					'@Ofload/AdminFeatureFlags',
	FLAGS_LOADING:			'@Ofload/AdminFeatureFlagsLoading',
	FLAGS_ERROR:			'@Ofload/AdminFeatureFlagsError',
	SEARCH:					'@Ofload/AdminFeatureFlagsSearch',
	VIEW_MODE:				'@Ofload/AdminFeatureFlagsViewMode',
	SELECTED:				'@Ofload/AdminFeatureFlagsSelected',
	ENRICHED:				'@Ofload/AdminFeatureFlagsEnriched',
	FILTERED:				'@Ofload/AdminFeatureFlagsFiltered',
	USER_DIRECTORY:			'@Ofload/AdminFlagUserDirectory',
	USER_DIRECTORY_LOADING:	'@Ofload/AdminFlagUserDirectoryLoading',
}
/* eslint-enable */

export const AdminFeatureFlags = atom<IAdminFeatureFlag[]>({
	key: keys.FLAGS,
	default: []
});

export const AdminFeatureFlagsLoading = atom<boolean>({
	key: keys.FLAGS_LOADING,
	default: false
});

export const AdminFeatureFlagsError = atom<string | null>({
	key: keys.FLAGS_ERROR,
	default: null
});

export const AdminFeatureFlagsSearch = atom<string>({
	key: keys.SEARCH,
	default: ''
});

export const AdminFeatureFlagsViewMode = atom<FLAG_VIEW_MODE>({
	key: keys.VIEW_MODE,
	default: FLAG_VIEW_MODE.TILE
});

export const AdminFeatureFlagsSelected = atom<string | null>({
	key: keys.SELECTED,
	default: null
});

export const AdminFlagUserDirectory = atom<IFlagPreviewer[]>({
	key: keys.USER_DIRECTORY,
	default: []
});

export const AdminFlagUserDirectoryLoading = atom<boolean>({
	key: keys.USER_DIRECTORY_LOADING,
	default: false
});

/* Composes AdminFeatureFlags (raw, often just IDs from list-previewers)
   with AdminFlagUserDirectory so previewers carry displayName/email.
   Falls back to the raw IDs when the user is unknown or the directory
   hasn't loaded. previewers === undefined (not fetched yet — previewers
   load lazily when a flag's dialog opens) is preserved so consumers can
   tell "not loaded" apart from "no previewers". */
export const EnrichedAdminFeatureFlags = selector<IAdminFeatureFlag[]>({
	key: keys.ENRICHED,
	get: ({ get }) => {
		const flags = get(AdminFeatureFlags);
		const users = get(AdminFlagUserDirectory);
		if (users.length === 0) return flags;
		const userMap = new Map(users.map((u) => [u.externalId, u]));
		return flags.map((f) => ({
			...f,
			previewers: f.previewers?.map((p) => {
				const known = userMap.get(p.externalId);
				return {
					externalId: p.externalId,
					displayName: p.displayName ?? known?.displayName,
					email: p.email ?? known?.email,
				};
			}),
		}));
	},
});

export const FilteredAdminFeatureFlags = selector<IAdminFeatureFlag[]>({
	key: keys.FILTERED,
	get: ({ get }) => {
		const flags = get(EnrichedAdminFeatureFlags);
		const query = get(AdminFeatureFlagsSearch).trim().toLowerCase();
		if (!query) return flags;
		return flags.filter((f) => f.name.toLowerCase().includes(query));
	},
});
